import axios from 'axios';
import { API_ENDPOINTS } from '@/config/api.config';
import { clearAuthClientStorage } from '@/utils/clearAuthClientStorage';

/** Default 60s — 10s was too low for slow networks / ELB / heavy list endpoints (shows as exact ~10s cancel in DevTools). */
const API_TIMEOUT_MS = (() => {
  const n = Number(import.meta.env.VITE_API_TIMEOUT_MS);
  return Number.isFinite(n) && n > 0 ? n : 60000;
})();

// Create axios instance
// Use empty baseURL when running with Vite proxy, or use STUDIO_BACKEND for direct access
const api = axios.create({
  baseURL: '', // Vite proxy handles /api routes
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(
            `${API_ENDPOINTS.STUDIO.AUTH}/refresh-token`,
            { refreshToken }
          );

          const { accessToken } = response.data.data;
          localStorage.setItem('auth_token', accessToken);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } else {
          // No refresh token — clear persisted auth too or Zustand rehydrates "logged in" and loops login ↔ dashboard
          clearAuthClientStorage();
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
      } catch (refreshError) {
        clearAuthClientStorage();
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

